import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';

interface EngineSectionProps {
  name: string;
  subtitle: string;
  color: string;
  children: React.ReactNode;
}

const EngineSection: React.FC<EngineSectionProps> = ({ name, subtitle, color, children }) => (
  <div style={{ marginBottom: 48 }}>
    <h2
      style={{
        fontSize: 28,
        fontWeight: 800,
        color,
        marginBottom: 4,
        letterSpacing: -0.5,
      }}
    >
      {name}
    </h2>
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 1,
        color: '#64748b',
        marginBottom: 16,
      }}
    >
      {subtitle}
    </div>
    <p style={{ fontSize: 16, lineHeight: 1.7, color: '#cbd5e1', maxWidth: 800 }}>{children}</p>
  </div>
);

interface EngineDetail {
  name: string;
  color: string;
  subtitle: string;
  description: string;
}

const engineDetails: EngineDetail[] = [
  {
    name: 'AutarLENS',
    subtitle: 'Layered Evaluation and Navigation System',
    color: '#60a5fa',
    description:
      "AutarLENS (Layered Evaluation and Navigation System) is the entry point for every repository Autar touches. It performs a deep, multi-agent analysis of a codebase, deploying specialized scanners in parallel to evaluate language ecosystems, CI/CD pipelines, testing strategies, security posture, documentation quality, dependency health, and AI tooling adoption. Each scanner operates independently, producing structured signals that feed directly into AutarGUARD and AutarTUNE for pattern matching and configuration recommendations. A full scan completes in minutes, not hours, giving teams an immediate, comprehensive profile of their repository's current state, strengths, and gaps. AutarLENS is designed to run on first connection and continuously thereafter, so the knowledge graph stays current as the codebase evolves. Think of it as the diagnostic that makes everything else in the Autar platform possible: before you can maintain, configure, or optimize, you need to deeply understand what you're working with.",
  },
  {
    name: 'AutarGUARD',
    subtitle: 'Graph-Unified Archetype Recognition & Diagnostics',
    color: '#a78bfa',
    description:
      'AutarGUARD (Graph-Unified Archetype Recognition & Diagnostics) is a structured map of every meaningful signal across thousands of analyzed repositories. The knowledge graph captures languages, frameworks, CI/CD patterns, test strategies, deployment targets, team size, package versions and scale indicators that are linked together so that when a new repo is ingested, the graph can pattern-match its characteristics against known archetypes and extract the optimal configuration. It compares patterns in similar repos that are used successfully in production, rather than relying on generic templates or manual setup. AutarGUARD applies the collective intelligence of the entire ecosystem into a personalized, scale-aware SDLC prescription for any given codebase.',
  },
  {
    name: 'AutarTUNE',
    subtitle: 'Tailored Usage and Normalization Engine',
    color: '#34d399',
    description: `AutarTUNE (Tailored Usage and Normalization Engine) is the intelligence layer that turns Autar's knowledge graph into actionable configuration decisions. By analyzing patterns across thousands of repositories, it learns which tools, workflows, and settings correlate with success for specific repo archetypes, then surfaces those preferences as recommendations when a new repo is onboarded. For example, AutarTUNE recognizes that a large TypeScript monorepo with 50+ packages shares structural DNA with projects like Angular and Nx, so it recommends Bazel over Turborepo, strict trunk-based development over GitFlow, and integration-heavy test strategies over unit-only coverage. Rather than applying universal "best practices," AutarTUNE understands that the right configuration depends on context: team size, deployment target, dependency graph complexity, and ecosystem norms. It is the mechanism that makes Autar's recommendations feel like they came from an engineer who has already solved your exact problem at scale.`,
  },
  {
    name: 'AutarMaTE',
    subtitle: 'Maintenance Tasks Engine',
    color: '#fbbf24',
    description:
      'AutarMaTE (Maintenance Tasks Engine) handles every maintenance action through a disciplined, auditable workflow: it creates a dedicated branch, pushes changes, runs the full validation suite, and reassesses impact before ever surfacing the result to the user. This process is fully customizable to fit within your existing sprint cadence, whether that means slotting into two-week cycles, daily deploys, or trunk-based development. Trust levels let you dial the automation from conservative (create a PR for human review) all the way to fully autonomous (validate and auto-merge when all signals pass). Every PR and commit is documented the way a senior engineer would do it: clear descriptions of what was changed, why, what was considered, and what the validation results confirmed. The goal is that a teammate reviewing an AutarMaTE PR can follow the reasoning as easily as if another developer on the team wrote it.',
  },
  {
    name: 'AutarAGENT',
    subtitle: 'Your AI-powered Virtual Dev',
    color: '#f472b6',
    description:
      "AutarAGENT sits alongside the entire cycle, acting as your always-available engineering partner. It interprets AutarLENS scan results in plain language, explains why AutarGUARD classified your repo the way it did, walks you through AutarTUNE's configuration recommendations, and guides you step-by-step through AutarMaTE's maintenance tasks. Think of it as a senior engineer who knows your entire codebase, never sleeps, and is always ready to pair. Whether you want to understand a critical vulnerability finding, debate a dependency upgrade strategy, or get hands-on help executing a complex migration, AutarAGENT is there. It's not a chatbot bolted on. It's the connective tissue that makes the other four components accessible to every developer, regardless of experience level.",
  },
];

export const AutarPlatformV2DescriptionsPage: React.FC = () => {
  const [selectedEngine, setSelectedEngine] = useState<EngineDetail | null>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedEngine(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <div
      style={{
        background: '#0a0a0f',
        minHeight: '100vh',
        color: '#ffffff',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <Navbar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '70px 24px 0' }}>
        <Link to="/brand" style={{ color: '#94a3b8', fontSize: 14, textDecoration: 'none' }}>
          ← Product Concepts
        </Link>
      </div>

      <div style={{ textAlign: 'center', paddingTop: 60, paddingBottom: 20 }}>
        <h1
          style={{
            fontSize: 42,
            fontWeight: 800,
            letterSpacing: -1,
            background: 'linear-gradient(135deg, #60a5fa, #a78bfa, #f472b6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 12,
          }}
        >
          The Autar Platform Descriptions
        </h1>
        <p style={{ fontSize: 18, color: '#64748b', fontWeight: 500 }}>
          Detailed engine definitions for the updated architecture
        </p>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 24px 0' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 10,
          }}
        >
          {engineDetails.map((engine) => (
            <button
              key={engine.name}
              type="button"
              onClick={() => setSelectedEngine(engine)}
              style={{
                border: `1px solid ${engine.color}99`,
                background: `${engine.color}22`,
                color: engine.color,
                borderRadius: 999,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: 0.2,
                cursor: 'pointer',
              }}
            >
              {engine.name}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px 80px' }}>
        <EngineSection
          name="AutarLENS"
          subtitle="Layered Evaluation and Navigation System"
          color="#60a5fa"
        >
          AutarLENS (Layered Evaluation and Navigation System) is the entry point for every
          repository Autar touches. It performs a deep, multi-agent analysis of a codebase,
          deploying specialized scanners in parallel to evaluate language ecosystems, CI/CD
          pipelines, testing strategies, security posture, documentation quality, dependency health,
          and AI tooling adoption. Each scanner operates independently, producing structured signals
          that feed directly into AutarGUARD and AutarTUNE for pattern matching and configuration
          recommendations. A full scan completes in minutes, not hours, giving teams an immediate,
          comprehensive profile of their repository's current state, strengths, and gaps. AutarLENS
          is designed to run on first connection and continuously thereafter, so the knowledge graph
          stays current as the codebase evolves. Think of it as the diagnostic that makes everything
          else in the Autar platform possible: before you can maintain, configure, or optimize, you
          need to deeply understand what you're working with.
        </EngineSection>

        <EngineSection
          name="AutarGUARD"
          subtitle="Graph-Unified Archetype Recognition & Diagnostics"
          color="#a78bfa"
        >
          AutarGUARD (Graph-Unified Archetype Recognition & Diagnostics) is a structured map of
          every meaningful signal across thousands of analyzed repositories. The knowledge graph
          captures languages, frameworks, CI/CD patterns, test strategies, deployment targets, team
          size, package versions and scale indicators that are linked together so that when a new
          repo is ingested, the graph can pattern-match its characteristics against known archetypes
          and extract the optimal configuration. It compares patterns in similar repos that are used
          successfully in production, rather than relying on generic templates or manual setup.
          AutarGUARD applies the collective intelligence of the entire ecosystem into a
          personalized, scale-aware SDLC prescription for any given codebase.
        </EngineSection>

        <EngineSection
          name="AutarTUNE"
          subtitle="Tailored Usage and Normalization Engine"
          color="#34d399"
        >
          AutarTUNE (Tailored Usage and Normalization Engine) is the intelligence layer that turns
          Autar's knowledge graph into actionable configuration decisions. By analyzing patterns
          across thousands of repositories, it learns which tools, workflows, and settings correlate
          with success for specific repo archetypes, then surfaces those preferences as
          recommendations when a new repo is onboarded. For example, AutarTUNE recognizes that a
          large TypeScript monorepo with 50+ packages shares structural DNA with projects like
          Angular and Nx, so it recommends Bazel over Turborepo, strict trunk-based development over
          GitFlow, and integration-heavy test strategies over unit-only coverage. Rather than
          applying universal "best practices," AutarTUNE understands that the right configuration
          depends on context: team size, deployment target, dependency graph complexity, and
          ecosystem norms. It is the mechanism that makes Autar's recommendations feel like they
          came from an engineer who has already solved your exact problem at scale.
        </EngineSection>

        <EngineSection name="AutarMaTE" subtitle="Maintenance Tasks Engine" color="#f472b6">
          AutarMaTE (Maintenance Tasks Engine) handles every maintenance action through a
          disciplined, auditable workflow: it creates a dedicated branch, pushes changes, runs the
          full validation suite, and reassesses impact before ever surfacing the result to the user.
          This process is fully customizable to fit within your existing sprint cadence, whether
          that means slotting into two-week cycles, daily deploys, or trunk-based development. Trust
          levels let you dial the automation from conservative (create a PR for human review) all
          the way to fully autonomous (validate and auto-merge when all signals pass). Every PR and
          commit is documented the way a senior engineer would do it: clear descriptions of what was
          changed, why, what was considered, and what the validation results confirmed. The goal is
          that a teammate reviewing an AutarMaTE PR can follow the reasoning as easily as if another
          developer on the team wrote it.
        </EngineSection>

        <EngineSection name="AutarAGENT" subtitle="Your AI-powered Virtual Dev" color="#fbbf24">
          AutarAGENT sits alongside the entire cycle, acting as your always-available engineering
          partner. It interprets AutarLENS scan results in plain language, explains why AutarGUARD
          classified your repo the way it did, walks you through AutarTUNE's configuration
          recommendations, and guides you step-by-step through AutarMaTE's maintenance tasks. Think
          of it as a senior engineer who knows your entire codebase, never sleeps, and is always
          ready to pair. Whether you want to understand a critical vulnerability finding, debate a
          dependency upgrade strategy, or get hands-on help executing a complex migration,
          AutarAGENT is there. It's not a chatbot bolted on. It's the connective tissue that makes
          the other four components accessible to every developer, regardless of experience level.
        </EngineSection>
      </div>

      <Footer />

      {selectedEngine && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="engine-modal-title"
          onClick={() => setSelectedEngine(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(2, 6, 23, 0.82)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            zIndex: 2000,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: 'min(760px, 100%)',
              background: '#0f172a',
              border: `1px solid ${selectedEngine.color}66`,
              borderRadius: 16,
              boxShadow: '0 24px 48px rgba(2, 6, 23, 0.45)',
              padding: 24,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div>
                <h3
                  id="engine-modal-title"
                  style={{ margin: 0, fontSize: 28, fontWeight: 800, color: selectedEngine.color }}
                >
                  {selectedEngine.name}
                </h3>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    color: '#94a3b8',
                  }}
                >
                  {selectedEngine.subtitle}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedEngine(null)}
                style={{
                  background: '#1e293b',
                  color: '#e2e8f0',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
            <p
              style={{
                marginTop: 16,
                marginBottom: 0,
                fontSize: 16,
                lineHeight: 1.7,
                color: '#cbd5e1',
              }}
            >
              {selectedEngine.description}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutarPlatformV2DescriptionsPage;
